import React, { useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { func } from 'prop-types';
import { Box, Typography } from '@mui/material';

// Utils
import { calculateSuccessfulResults } from 'feature/loading-management/utils/calculate';

import Buttons from '../../components/Buttons';
import SequencingProgress from '../../components/SequencingProgress';
import SubmitProgressHeader from '../../components/SubmitProgressHeader';
import { SequencingWizardContext } from '../../SequencingWizardContext';

const RemoveStep6 = props => {
  const { close, removeBatchSequence } = props;
  const isMounted = useRef(true);
  const { viewBy } = useParams();

  const {
    batchUpdating,
    data: {
      changeList,
      currentAffiliate,
      currentSelection,
      futureDate,
      inventoryOwners,
      locations,
      products,
    },
    initialData,
    progress,
    setCurrentStep,
    setData,
    setProgress,
  } = useContext(SequencingWizardContext);
  const { dataCompanyId, customerIdName } = currentAffiliate;

  const handleStartAnother = () => {
    setData(initialData);
    setCurrentStep(0);
  };

  useEffect(() => {
    return () => setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {
      companyCustomerIdName: customerIdName,
      currentSelection,
      dataCompanyId,
      futureSequenceDate: futureDate,
      inventoryOwners,
      locations,
      products,
      viewBy,
    };
    removeBatchSequence(params).then(result => {
      isMounted.current &&
        setData(prevData => ({
          ...prevData,
          changeList: result,
        }));
      isMounted.current && setProgress(100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successfulResults = calculateSuccessfulResults(changeList);

  return (
    <>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          display: 'flex',
          flexDirection: 'column',
          // height of other components is 157px
          // prevents progress bar from looking off
          height: 'calc(100% - 157px)',
          overflowY: 'auto',
          pt: 3,
        }}
      >
        <SubmitProgressHeader totalProgress={progress} />

        <Typography
          sx={{ marginBottom: 2, marginTop: 4.5, padding: '0 16px', textAlign: 'center' }}
          variant="body1"
        >
          {!batchUpdating
            ? `The following Inventory Owners have been removed`
            : `Removing the following Inventory Owners`}{' '}
          from the Authorization Sequences for the selected products and locations.
        </Typography>
        {!batchUpdating && (
          <Typography sx={{ mb: 2, px: 2, py: 0, textAlign: 'center' }} variant="body1">
            A total of{' '}
            {successfulResults === 1
              ? `${successfulResults} change was`
              : `${successfulResults} changes were`}{' '}
            made. This change can take up to 15 minutes to appear in the application.
          </Typography>
        )}
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'graphic.lighter',
            borderBottom: '0.1em solid',
            borderBottomColor: 'graphic.lighter',
            borderTop: '0.1em solid',
            borderTopColor: 'graphic.lighter',
            display: 'flex',
            minHeight: 48,
            px: 3,
            py: 0,
            width: '100%',
            zIndex: 4,
          }}
        >
          <Typography sx={{ color: 'primary.dark' }} variant="subtitle1">
            Summary
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography sx={{ color: 'text.secondary', fontWeight: 'bold' }} variant="body1">
            {`Selected Inventory Owner${inventoryOwners.length > 1 ? 's' : ''}`}
          </Typography>
        </Box>
        {inventoryOwners.map(({ companyCustomerIdName: id, nameLong }) => (
          <Box
            key={id}
            sx={{
              '&:not(:last-of-type)': {
                borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
              },
              bgcolor: 'graphic.lighter',
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="body1">{nameLong}</Typography>
          </Box>
        ))}

        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography sx={{ color: 'text.secondary', fontWeight: 'bold' }} variant="body1">
            Updating Sequences
          </Typography>
        </Box>
        <Box bgcolor="graphic.lighter" height="100%">
          <SequencingProgress />
        </Box>
      </Box>
      <Buttons
        handleLeftButtonClick={handleStartAnother}
        handleRightButtonClick={close}
        leftLabel="Start Another"
        loadingData={batchUpdating}
        rightLabel="Close"
      />
    </>
  );
};

RemoveStep6.propTypes = {
  close: func.isRequired,
  removeBatchSequence: func.isRequired,
};

export default RemoveStep6;
